import { Pipe, PipeTransform } from '@angular/core';
import {
  ContextService,
  LocaleService,
} from '@maximizer/core/shared/data-access';
import {
  isToday,
  isYesterday,
  isTomorrow,
  parseISO,
  isValid,
  differenceInCalendarDays,
  differenceInWeeks,
  differenceInMonths,
} from 'date-fns';
import { ResourceOrTranslatePipe } from './resource-or-translate.pipe';

@Pipe({
  name: 'relativeDate',
  pure: false,
})
export class RelativeDatePipe
  extends ResourceOrTranslatePipe
  implements PipeTransform
{
  constructor(
    private readonly locale: LocaleService,
    context: ContextService,
  ) {
    super(context);
  }

  override transform(
    isoDate: string,
    mode: 'timeAgo' | 'default' = 'default',
    defaultFormat?: string,
  ): string {
    if (!isoDate) {
      return '';
    }

    const date = parseISO(isoDate);

    if (!isValid(date)) {
      return '';
    }

    if (mode === 'timeAgo') {
      return this.formatTimeAgo(date);
    }

    return this.formatDefault(date, isoDate, defaultFormat);
  }

  private formatDefault(
    date: Date,
    isoDate: string,
    defaultFormat?: string,
  ): string {
    const hasTime = isoDate.includes('T');

    let result: string;
    let isRelative = false;

    if (isToday(date)) {
      isRelative = true;
      result = super.transform('JSS_GenTxtToday', 'dates.today');
    } else if (isYesterday(date)) {
      isRelative = true;
      result = super.transform('JSS_GenTxtYesterday', 'dates.yesterday');
    } else if (isTomorrow(date)) {
      isRelative = true;
      result = super.transform('JSS_GenTxtTomorrow', 'dates.tomorrow');
    } else {
      result = this.locale.formatDate(
        date,
        defaultFormat ?? (hasTime ? 'userDateTime' : 'userDate'),
      );
    }

    if (isRelative && hasTime) {
      result = `${result} ${this.locale.formatTimeUserLocale(date)}`;
    }

    return result;
  }

  private formatTimeAgo(date: Date): string {
    if (isToday(date)) {
      return super.transform('JSS_GenTxtToday', 'dates.today');
    }

    const differenceInDays = differenceInCalendarDays(date, new Date());
    const days = Math.abs(differenceInDays);
    const isPast = differenceInDays < 0;

    if (days === 0) {
      return super.transform('JSS_GenTxtToday', 'dates.today');
    }

    if (days === 1) {
      return isPast
        ? super.transform('JSS_GenTxtYesterday', 'dates.yesterday')
        : super.transform('JSS_GenTxtTomorrow', 'dates.tomorrow');
    }

    if (Math.abs(differenceInMonths(date, new Date())) >= 2) {
      return this.locale.formatDate(date, 'userDate');
    }

    if (days < 7) {
      return this.replaceCount(
        isPast ? 'dates.relative.daysAgo' : 'dates.relative.daysAfter',
        days,
      );
    }

    const absWeeks = Math.abs(differenceInWeeks(date, new Date()));
    if (absWeeks >= 1 && absWeeks <= 4) {
      return this.replaceCount(
        isPast ? 'dates.relative.weeksAgo' : 'dates.relative.weeksAfter',
        absWeeks,
      );
    }

    return isPast
      ? super.transform('', 'dates.relative.aMonthAgo')
      : super.transform('', 'dates.relative.aMonthAfter');
  }

  private replaceCount(key: string, count: number): string {
    return super.transform('', key).replace('{{count}}', String(count));
  }
}
